import type React from 'react'
import { type ReactNode, useEffect } from 'react'
import { CloseIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import { Button, type ButtonProps } from './Button'
import { Text } from './Text'

export interface ModalProps {
	isOpen: boolean
	onClose: () => void
	title: string
	fullContent?: boolean
	fullScreen?: boolean
	hideHeader?: boolean
	children?: ReactNode
	buttons?: ButtonProps[]
	buttonsAlignment?: 'right' | 'full'
	className?: string
	bodyClassName?: string
	closeOnOverlayClick?: boolean
}

const modalOverlayVariants = cva(
	'fixed inset-0 bg-neutral-900/50 flex items-center justify-center z-100 transition-opacity',
	{
		variants: {
			isOpen: {
				true: 'opacity-100',
				false: 'opacity-0 pointer-events-none',
			},
			fullScreen: {
				true: 'items-start justify-start',
				false: 'items-center justify-center',
			},
		},
		defaultVariants: {
			isOpen: false,
			fullScreen: false,
		},
	},
)

const modalContainerVariants = cva(
	'bg-neutral-0 text-left rounded-lg shadow-lg transition-all flex flex-col max-h-[90vh]',
	{
		variants: {
			isOpen: {
				true: 'transform scale-100',
				false: 'transform scale-95',
			},
			fullScreen: {
				true: 'absolute w-full h-full rounded-none max-h-full',
				false: 'w-85 md:w-135 mx-4',
			},
		},
		defaultVariants: {
			isOpen: false,
			fullScreen: false,
		},
	},
)

const buttonContainerVariants = cva('flex gap-3 p-6', {
	variants: {
		alignment: {
			right: 'justify-end',
			full: 'flex-col sm:flex-row',
		},
	},
	defaultVariants: {
		alignment: 'right',
	},
})

export const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	children,
	buttons,
	fullContent = false,
	fullScreen = false,
	hideHeader = false,
	buttonsAlignment = 'right',
	className,
	bodyClassName,
	closeOnOverlayClick = true,
}) => {
	useEffect(() => {
		const handleEscapeKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onClose()
			}
		}

		document.addEventListener('keydown', handleEscapeKey)
		return () => {
			document.removeEventListener('keydown', handleEscapeKey)
		}
	}, [isOpen, onClose])

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
		}
		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isOpen])

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (closeOnOverlayClick && e.target === e.currentTarget) {
			onClose()
		}
	}

	return (
		<div
			className={modalOverlayVariants({ isOpen, fullScreen })}
			onClick={handleOverlayClick}
			onKeyDown={(e) => {
				if (e.key === 'Escape') onClose()
			}}
			role='dialog'
			aria-modal='true'
			aria-labelledby='modal-title'
			data-state={isOpen ? 'open' : 'closed'}
			data-fullscreen={fullScreen ? 'true' : 'false'}>
			<div className={cn(modalContainerVariants({ isOpen, fullScreen }), className)}>
				{hideHeader && (
					<span id='modal-title' className='sr-only'>
						{title}
					</span>
				)}
				{!hideHeader && (
					<>
						<div className='py-5 px-6 flex justify-between items-center'>
							<Text id='modal-title' variant='medium' weight='semibold' className='capitalize'>
								{title}
							</Text>
							<button
								type='button'
								onClick={(e) => {
									e.preventDefault()
									onClose()
								}}
								className='text-neutral-500 hover:text-neutral-700 transition-colors p-1 rounded-full hover:bg-neutral-100 cursor-pointer'
								aria-label='Close modal'>
								<CloseIcon />
							</button>
						</div>
						<div className='h-px bg-neutral-100 w-full' />
					</>
				)}
				{isOpen && (
					<div
						className={cn(
							'p-6 flex-1 min-h-0',
							fullContent ? 'p-0 overflow-hidden flex flex-col' : 'overflow-y-auto',
							bodyClassName,
						)}>
						{children}
					</div>
				)}

				{buttons && (
					<>
						<div className='h-px bg-neutral-100 w-full' />
						<div className={buttonContainerVariants({ alignment: buttonsAlignment })}>
							{buttons?.map(({ variant, onClick, className, ...button }, index) => (
								<Button
									// biome-ignore lint/suspicious/noArrayIndexKey: modal buttons have no stable identifier
									key={index}
									variant={variant || 'secondary'}
									onClick={onClick}
									className={cn('flex-1 md:flex-none', className)}
									{...button}>
									{button.children}
								</Button>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	)
}
